import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { DirtyCheckPlugin } from '@datorama/akita/src/lib/plugins/dirtyCheck/dirtyCheckPlugin';
import { ApplianceDetailsStore } from '@modules/appliance-details/state/appliance-details.store';
import { DentureDetailsStore } from '@modules/denture-details/state/denture-details.store';
import { OrderFormConfirmationService } from '@modules/order/services/order-form-confirmation.service';
import { ProcedureConfigurationService } from '@modules/order/services/procedure-configuration.service';
import { OrderQuery } from '@modules/order/state/order-query';
import { OrderStore } from '@modules/order/state/order-store';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { of, map, Observable } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class ProcedureTypeService {
	private static readonly procedureIdsRequiringTeethReset: ProcedureEnum[] = [
		ProcedureEnum.ImplantPlanning,
		ProcedureEnum.Denture_Removable
	];

	private readonly teethDiagramDirtyCheck: DirtyCheckPlugin;

	constructor(
		private readonly orderStore: OrderStore,
		private readonly orderQuery: OrderQuery,
		private readonly procedureConfigurationService: ProcedureConfigurationService,
		private readonly orderFormConfirmationService: OrderFormConfirmationService,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly teethDiagramStore: TeethDiagramStore,
		private readonly dentureDetailsStore: DentureDetailsStore,
		private readonly applianceDetailsStore: ApplianceDetailsStore
	) {
		this.teethDiagramDirtyCheck = new DirtyCheckPlugin(this.teethDiagramQuery).setHead();
	}

	handleProcedureTypeChanged(procedureTypeId: number): Observable<boolean> {
		const currentProcedureMap = this.orderQuery.procedureMap;

		if (!currentProcedureMap) {
			return of(true);
		}

		const currentProcedureType = currentProcedureMap.TypeId;

		if (procedureTypeId === currentProcedureType) {
			return of(true);
		}

		const procedureId = currentProcedureMap.ProcedureId;

		const currentSendTo = this.orderQuery.sendTo;
		const procedureMap =
			this.procedureConfigurationService.getProcedureMap(procedureId, procedureTypeId, currentSendTo) ??
			this.procedureConfigurationService.getProcedureMap(procedureId, procedureTypeId);

		const availableLabs = this.procedureConfigurationService.getAvailableLabs({
			procedureId,
			typeId: procedureTypeId
		});
		const shouldResetSendTo = !availableLabs?.some(lab => lab?.Id === currentSendTo?.Id);
		const orderPatch = {
			procedureMap,
			availableLabs,
			...(shouldResetSendTo ? { sendTo: null } : {})
		};

		const isTeethResetRequired =
			this.requiresTeethResetOnTypeChange(procedureMap?.ProcedureId) && this.teethDiagramDirtyCheck.isDirty();
		const canProceed$ = isTeethResetRequired
			? this.orderFormConfirmationService.openPopUp$(true).pipe(map(dialogResult => dialogResult === PopUpActions.Ok))
			: of(true);

		return canProceed$.pipe(
			map(allowed => {
				// prevent update store if user does not allow to reset teeth
				if (!allowed) {
					return false;
				}

				if (isTeethResetRequired) {
					this.teethDiagramStore.resetTeeth();
					this.teethDiagramDirtyCheck.setHead();
				}
				this.resetStoresAfterProcedureTypeChange();
				this.orderStore.update(orderPatch);

				return true;
			})
		);
	}

	/** Resets the stores whose values depend on the selected procedure type. */
	resetStoresAfterProcedureTypeChange(): void {
		this.dentureDetailsStore.update({ isUpperJawChecked: false, isLowerJawChecked: false });
		this.applianceDetailsStore.resetType();
	}

	/** Whether changing the procedure type of this procedure invalidates the teeth diagram. */
	requiresTeethResetOnTypeChange(procedureId: number | undefined): boolean {
		return ProcedureTypeService.procedureIdsRequiringTeethReset.includes(procedureId as ProcedureEnum);
	}
}
